import './index.scss'
import {useEffect, useState} from 'react'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageTitle} from '../../components/core/title'
import Footer from '../../components/layouts/footer'
import {EditIcon, HelpIcon, HorizontalMoreIcon, PlusIcon, ReminderIcon, TrashIcon} from '../../icons'
import {ImgButton} from '../../components/core/button'
import Table from '../../components/core/table'
import MemberForm from './MemberForm'
import Modal from '../../components/core/modal'
import {useDispatch, useSelector} from 'react-redux'
import {deleteMember, getActiveMembers, getInvitationsMembers, remindMember} from '../../stores/members'
import {DateFormats, formatDate} from '../../utils/date'
import ContentLoader from '../../components/core/loader'
import TextHelper from '../../utils/text'
import Confirm from '../../components/core/confirm'
import {setConfirmIsActive} from '../../stores/alert'
import Customer from '../../utils/customer'
import EmptyContent from '../resources/EmptyContent'
import Badge from '../../components/core/badge'
import Dropdown from '../../components/core/dropdown'
import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import {useNavigate, useParams} from 'react-router-dom'
import UpdateModal from './UpdateModal'
import roles from '../../data/roles.json'
import {getTeams, setSelectedTeamId} from '../../stores/teams'
import CustomSelect from '../../components/core/customSelect'
import {getServices} from '../../stores/resources'
import Tooltip from '../../components/core/tooltip'
import {routes} from '../../routes'
import Navigation from '../../utils/navigation'
import Breadcrumb from '../../components/core/breadcrumb'
import {colors} from '../../utils/colors'
import classNames from 'classnames'
import {sortBySelected} from '../../utils/sorting'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'

const SORT_ID = 'members'

const {isReadOnlyModeEnabled, isIntegrationEnabled} = envConfig

const Members = () => {
    const {teamId} = useParams()
    const text = useNamespaceTranslations('members')
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const tabItem = (label, value) => {
        return {label, value}
    }
    const tabs = {
        active: tabItem(text.active, 'active'),
        invitations: tabItem(text.invitations, 'invitations')
    }

    const tabsArr = Object.values(tabs)
    const isTabActive = item => item.value === activeTab

    const [activeTab, setActiveTab] = useState(tabs.active.value)
    const [memberModalIsActive, setMemberModalIsActive] = useState(false)
    const [updateModal, setUpdateModal] = useState(false)
    const [invitationsMembers, setInvitationMembers] = useState([])
    const [isFetching, setIsFetching] = useState(true)
    const [selectedMembers, setSelectedMembers] = useState([])
    const [sortedMembers, setSortedMembers] = useState([])
    const [isDeleting, setIsDeleting] = useState(false)
    const [clickedMember, setClickedMember] = useState(null)
    const dispatch = useDispatch()

    const teams = useSelector(state => state.teams.teams)
    const activeMembers = useSelector(state => state.members.members)
    const localTeams = teams?.map(item => {
        return {
            ...item,
            label: <span className="d-flex justify-content-between align-items-center gap-half">
                {item.label}
                {item?.is_member && <Badge color="success">Current</Badge>}
            </span>
        }
    })
    const member = Array.isArray(activeMembers) ? activeMembers?.find(item => item?.member_id === selectedMembers[0]) : null
    const team = teams?.find(item => item?.value === teamId)
    const navigate = useNavigate()

    const isRootMember = Customer.isRoot()
    const isManager = Customer.isManager()

    const hasAccess = (rootCondition = true, managerCondition = true) => {
        if (isManager && team?.is_member && managerCondition) {
            return true
        }

        return isRootMember && rootCondition
    }

    const getFullName = member => {
        return `${member?.name} ${member?.surname}`
    }

    const customFieldLogic = {
        name: (item) => {
            return getFullName(item)
        }
    }

    const getRemindButtonStatus = member => {
        return member?.status !== text.invitation_statuses.pending
    }

    const getDeleteButtonStatus = (isRoot, isManager, isCurrent, teamMember) => {
        return !isCurrent && ((isRoot && !isManager) || (isManager && teamMember?.role !== roles.root.value))
    }

    const getActionButton = member => {
        return <Dropdown menuButton={<button className={classNames('table-action-btn', {
            dark: isDarkMode
        })}><HorizontalMoreIcon/></button>}>
            {member?.channel && hasAccess() && (
                <>
                    <MenuItem disabled={getRemindButtonStatus(member)} onClick={() => remindMembers(member?.channel)}>
                        <span className="d-flex">
                            <ReminderIcon/>
                        </span>
                        <span>{text.remind}</span>
                    </MenuItem>
                </>
            )}
            {!member?.channel && (
                <>
                    {hasAccess(true, false) && (
                        <MenuItem onClick={() => {
                            setClickedMember(member)
                            setUpdateModal(true)
                        }} disabled={isReadOnlyModeEnabled}>
                            <span className="d-flex">
                                <EditIcon/>
                            </span>
                            <span>{text.edit}</span>
                        </MenuItem>
                    )}
                </>
            )}
            {getDeleteButtonStatus(isRootMember, isManager, Customer.isCurrentUser(member?.customer_id), member) && (
                <>
                    {hasAccess() || hasAccess(true, false) && <MenuDivider/>}
                    <MenuItem className="danger" onClick={() => {
                        setClickedMember(member)
                        dispatch(setConfirmIsActive(true))
                    }
                    } disabled={isReadOnlyModeEnabled}>
                        <span className="d-flex"><TrashIcon stroke={colors.colorRed}/></span>
                        <span>{text.delete}</span>
                    </MenuItem>
                </>
            )}
        </Dropdown>
    }

    const activeMembersTbodyData = Array.isArray(sortedMembers) && sortedMembers.map(item => {
        return [<span className="min-width">{getFullName(item)}</span>,
            <span className="min-width"
                title={item?.email}>{TextHelper.textLengthChecker(item?.email, 32)}</span>,
            <span className="min-width">
                <Badge color={roles[item?.role].color} className="capitalize">{item?.role}</Badge>
            </span>,
            <span className="flex-1">
                <span>
                    {formatDate(item?.created_at, DateFormats.SLASH_DMY_TIME_SHORT)}
                </span>
                {(isRootMember || (isManager && !Customer.isCurrentUser(item?.customer_id) && item?.role !== roles.root.value)) && getActionButton(item)}
            </span>
        ]
    })
    const invitationsMembersTbodyData = Array.isArray(invitationsMembers) && invitationsMembers?.map(item => {
        return [
            <span className="min-width" title={item?.channel}>{TextHelper.textLengthChecker(item?.channel, 21)}</span>,
            <span className="min-width">{<Badge
                color={item?.status === text.invitation_statuses.pending ? 'muted' : 'success'}>{TextHelper.capitalizeFirstChar(item?.status)}</Badge>}</span>,
            <span className="min-width">{item?.sender_full_name}</span>,
            <span className="min-width">{formatDate(item?.created_at, DateFormats.SLASH_DMY_TIME_SHORT)}</span>,
            <span className="flex-1">
                <span>
                    {item?.reminded_at ? formatDate(item?.reminded_at, DateFormats.SLASH_DMY_TIME_SHORT) : 'N/A'}
                </span>
                {(isRootMember || (isManager && !Customer.isCurrentUser(item?.customer_id))) && getActionButton(item)}
            </span>
        ]
    })

    const generateTabMenu = () => {
        return (
            <div className={classNames('sections', {
                dark: isDarkMode
            })}>
                {tabsArr.map((item, index) => (
                    <span onClick={() => setActiveTab(item.value)}
                        className={classNames('section-text', {
                            active: isTabActive(item),
                            dark: isDarkMode
                        })}
                        key={index}>{item.label}</span>
                ))}
            </div>
        )
    }

    const generateActiveTabBody = () => {
        return (
            <div className="active-members">
                {activeMembers?.length ? (
                    <Table
                        strech
                        tableHeader={text.activeTabTableHeader}
                        tbodyData={activeMembersTbodyData}
                        identificator='customer_id'
                        data={sortedMembers}
                        isMultiCheckable={false}
                        selectedData={selectedMembers}
                        setSelectedData={data => setSelectedMembers(data)}
                        isSortingEnabled
                        setData={setSortedMembers}
                        sortId={SORT_ID}
                        customFieldLogic={customFieldLogic}
                        originalData={activeMembers}
                    />
                ) : (
                    <EmptyContent message={text.empty_members} hideButton={true}/>
                )}
            </div>
        )
    }
    const generateInvitationsTabBody = () => {
        return (
            <div>
                {invitationsMembers?.length ? (
                    <Table
                        strech
                        isScrollable
                        tableHeader={text.invitationsTabTableHeader}
                        tbodyData={invitationsMembersTbodyData}
                        data={invitationsMembers}
                        identificator='channel'
                        isMultiCheckable={false}
                        selectedData={selectedMembers}
                        setSelectedData={data => setSelectedMembers(data)}
                        isSortingEnabled
                        setData={setInvitationMembers}
                        sortId="invitations"
                        originalData={invitationsMembers}
                    />
                ) : (
                    <EmptyContent message={text.empty_invitations} hideButton={true}/>
                )}
            </div>)
    }

    const fetchActiveMembers = async() => {
        await dispatch(getActiveMembers(teamId))
    }

    const fetchInvitationsMembers = async() => {
        const res = await dispatch(getInvitationsMembers(teamId))
        setInvitationMembers(res)
    }

    const fetchTeams = async() => {
        await dispatch(getTeams())
    }

    const remindMembers = async(channel) => {
        const channelValue = typeof channel === 'string' ? channel : null
        const res = await dispatch(remindMember(teamId, channelValue || selectedMembers[0]))
        res && fetchInvitationsMembers()
        setClickedMember(null)
    }

    const deleteMembers = async() => {
        setIsDeleting(true)
        const customerId = clickedMember?.customer_id
        const channelValue = clickedMember?.channel
        const id = customerId || channelValue || selectedMembers[0]
        const res = await dispatch(deleteMember(teamId, id, activeTab))
        if (res) {
            isTabActive(tabs.active) ? fetchActiveMembers() : fetchInvitationsMembers()
        }
        dispatch(setConfirmIsActive(false))
        setIsDeleting(false)
        setClickedMember(null)
    }

    const closeModal = () => {
        setMemberModalIsActive(false)
        setMemberModalIsActive(false)
    }
    const closeUpdateModal = () => {
        setClickedMember(null)
        setUpdateModal(false)
        fetchActiveMembers()
    }

    const openModal = () => {
        setMemberModalIsActive(true)
    }

    const getTeamMemberCount = () => {
        return (activeMembers?.length || 0) + (invitationsMembers?.length || 0)
    }

    const fetchData = async() => {
        setIsFetching(true)
        await Promise.all([fetchTeams(), fetchActiveMembers(), fetchInvitationsMembers()])
        setIsFetching(false)
    }

    const Header = () => {
        return (
            team ? <div className="header">
                <div className="data-switcher-container">
                    <CustomSelect
                        containerClassName="w-auto"
                        placeholder={text.team}
                        options={localTeams}
                        name="team_id" value={team}
                        onChange={(_, data) => {
                            navigate(Navigation.getNavigationUrl(Navigation.getLocalizedPath(routes.members.paths), ':teamId', data?.value))
                            dispatch(setSelectedTeamId(data.value))
                            dispatch(getServices())
                        }}/>
                    <Tooltip placement="right" interactive trigger="click"
                        content={text.info_text}>
                        <HelpIcon/>
                    </Tooltip>
                </div>
                {hasAccess() && (
                    <ImgButton onClick={openModal}
                        className="responsive-add-button">
                        <span><PlusIcon/></span>
                    </ImgButton>
                )}
            </div> : null
        )
    }

    useEffect(() => {
        setSelectedMembers([])
        fetchData()
    }, [activeTab, teamId])

    useEffect(() => {
        setSortedMembers(sortBySelected(activeMembers, SORT_ID, text.activeTabTableHeader))
    }, [activeMembers])

    return (
        <main className={classNames('main members', {
            dark: isDarkMode
        })}>
            <Modal
                title={text.add_new_member}
                isOpen={memberModalIsActive}
                closeModal={closeModal}>
                <MemberForm
                    closeModal={closeModal}
                    fetchInvitationsMembers={fetchInvitationsMembers}
                    memberCount={getTeamMemberCount()}
                />
            </Modal>
            <Modal
                title={text.edit_member}
                isOpen={updateModal}
                closeModal={() => {
                    setClickedMember(null)
                    setUpdateModal(null)
                }}>
                <UpdateModal
                    teamId={teamId}
                    member={clickedMember || member}
                    teams={teams}
                    closeModal={closeUpdateModal}
                />
            </Modal>
            <Confirm
                body={text.are_you_sure}
                callback={deleteMembers} showLoader={isDeleting}/>
            <div>
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            {isIntegrationEnabled ?
                                <Breadcrumb data={[TextHelper.createSectionTitle(text.section), routes.teams]}/> :
                                <span className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.teams}</span>}
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                    {hasAccess() && (
                        <ImgButton onClick={openModal} disabled={isReadOnlyModeEnabled}>
                            {text.add_member}
                            <span><PlusIcon/></span>
                        </ImgButton>
                    )}
                </div>
                <Header/>
                {generateTabMenu()}
                {isFetching ? (
                    <ContentLoader/>
                ) : isTabActive(tabs.active) ? generateActiveTabBody() : generateInvitationsTabBody()}
            </div>
            <Footer/>
        </main>
    )
}

export default Members
